import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ShareEntryDialog from "@/components/ShareEntryDialog";
import { Heart, Share2, ArrowLeft, Trophy, LogIn } from "lucide-react";
import { toast } from "sonner";

/**
 * Public share-optimized page for a single contest entry.
 * URL: /contest/:cid/entry/:eid
 *
 * Design: mobile-first, photo-dominant, minimal chrome.
 */
export default function ContestEntryPage() {
  const { cid, eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/contest/entries/${eid}`);
      setEntry(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Participation introuvable");
      navigate(`/concours/${cid}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  const contestActive = entry?.contest?.status === "active";

  const handleVote = async () => {
    if (!user) {
      // Preserve the current URL so we come back after login
      const next = encodeURIComponent(window.location.pathname);
      navigate(`/login?next=${next}`);
      return;
    }
    if (entry?.is_owner) {
      toast.info("Vous ne pouvez pas voter pour votre propre participation");
      return;
    }
    if (!contestActive) {
      toast.info("Les votes sont clôturés pour ce concours");
      return;
    }
    setVoting(true);
    try {
      const { data } = await api.post(`/concours/${cid}/entries/${eid}/vote`);
      setEntry((e) => ({ ...e, has_voted: data.has_voted, votes_count: data.votes_count }));
      toast.success(data.has_voted ? "Merci pour votre vote !" : "Vote retiré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de voter");
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF3EA]">
        <div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!entry) return null;

  const u = entry.user || {};
  const c = entry.contest || {};
  const isOwner = !!entry.is_owner;
  const canShowShare = !!user && isOwner; // Only the author sees Share button on the public page

  return (
    <div className="min-h-screen bg-[#FAF3EA]">
      {/* Minimal top bar */}
      <header className="bg-white border-b border-[#EADDC9] sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#1A1A1A] tracking-wide" data-testid="entry-brand">
            Helencia
          </Link>
          {c.id && (
            <Link
              to={`/concours/${c.id}`}
              className="text-xs text-[#6E4B2A] hover:text-[#1A1A1A] inline-flex items-center gap-1.5"
              data-testid="entry-to-contest"
            >
              <Trophy className="w-3.5 h-3.5" /> Voir le concours
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 md:py-8">
        {/* Back (contextual) */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-xs text-[#6E4B2A] mb-4 hover:text-[#1A1A1A]"
          data-testid="entry-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour
        </button>

        {/* Photo */}
        <div className="rounded-3xl overflow-hidden bg-white border border-[#EADDC9] shadow-[0_20px_60px_-30px_rgba(110,75,42,0.25)]">
          <div className="relative bg-[#FAF3EA]">
            <img
              src={entry.image_url}
              alt={u.name || "Participation"}
              className="w-full h-auto object-cover max-h-[75vh]"
              data-testid="entry-image"
            />
            {/* Vote pill overlay — always visible on mobile */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-sm">
              <Heart className={`w-4 h-4 ${entry.has_voted ? "fill-[#C68E59] text-[#C68E59]" : "text-[#6E4B2A]"}`} />
              <span className="text-sm font-semibold text-[#1A1A1A]" data-testid="entry-vote-count">
                {entry.votes_count ?? 0}
              </span>
            </div>
          </div>

          {/* Meta */}
          <div className="p-5 md:p-6">
            <div className="flex items-center gap-3">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center text-sm font-semibold text-[#A26F3E]">
                  {(u.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-serif text-lg text-[#1A1A1A] leading-tight" data-testid="entry-talent-name">
                  {u.name || "Talent Helencia"}
                </div>
                {c.title && (
                  <div className="text-[11px] uppercase tracking-wider text-[#A26F3E] mt-0.5">
                    {c.title}
                  </div>
                )}
              </div>
            </div>

            {entry.description && (
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-4 whitespace-pre-line" data-testid="entry-description">
                {entry.description}
              </p>
            )}

            {/* Primary action: Vote */}
            <div className="mt-6 space-y-3">
              {!user ? (
                <Button
                  onClick={handleVote}
                  className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12 text-base"
                  data-testid="entry-vote-login-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Connectez-vous pour voter
                </Button>
              ) : isOwner ? (
                <div className="rounded-full bg-[#FAF3EA] text-[#6E4B2A] text-center py-3 text-sm border border-[#EADDC9]">
                  C'est votre participation
                </div>
              ) : !contestActive ? (
                <div className="rounded-full bg-[#FAF3EA] text-[#6E4B2A] text-center py-3 text-sm border border-[#EADDC9]">
                  Les votes sont clôturés
                </div>
              ) : (
                <Button
                  onClick={handleVote}
                  disabled={voting}
                  className={`w-full rounded-full h-12 text-base font-semibold transition ${
                    entry.has_voted
                      ? "bg-[#A26F3E] hover:bg-[#6E4B2A] text-white"
                      : "bg-[#C68E59] hover:bg-[#A26F3E] text-white"
                  }`}
                  data-testid="entry-vote-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${entry.has_voted ? "fill-current" : ""}`} />
                  {voting ? "…" : entry.has_voted ? "Vote retiré ?" : "Voter"}
                </Button>
              )}

              {canShowShare && (
                <Button
                  onClick={() => setShareOpen(true)}
                  variant="outline"
                  className="w-full rounded-full border-[#EADDC9] text-[#1A1A1A] h-12 text-base hover:bg-[#FAF3EA]"
                  data-testid="entry-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Partager ma participation
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tiny footer */}
        <p className="text-center text-[11px] text-[#8B8680] mt-6">
          Helencia — La mode se rencontre ici.
        </p>
      </main>

      <ShareEntryDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        entryId={eid}
        contestId={cid}
      />
    </div>
  );
}
